import React from 'react'
import { IQuizQuestion, QuizQuestion } from 'protos'

import MarkdownTypography from 'components/MarkdownTypography'
import QuizChoices from 'components/QuizChoices'
import T from 'components/FormattedMessage'

import { WithStyles, createStyles, Typography, ListItem, Grid, Button, withStyles, Theme } from '@material-ui/core'

interface Props {
  question: IQuizQuestion
  onAnswerSubmit: (answerId: string) => void
}

const styles = (theme: Theme) => createStyles({
  answersContainer: {
    backgroundColor: theme.palette.grey[100],
  },
  buttonContainer: {
    marginLeft: theme.spacing(),
  },
  icon: {
    marginRight: theme.spacing() * 2,
    height: 48,
  },
})

type InjectedProps = Props & WithStyles<typeof styles>

const QuizListItem: React.SFC<InjectedProps> = ({ question, onAnswerSubmit, classes }) => {
  const [isEditing, setEditing] = React.useState<boolean>(false)
  const [selectedAnswerId, setSelectedAnswerId] = React.useState<string | undefined | null>(question.selectedAnswerId)

  const startEditing = () => setEditing(true)
  const stopEditing = () => {
    if (selectedAnswerId && selectedAnswerId !== question.selectedAnswerId) {
      onAnswerSubmit(selectedAnswerId)
    }

    setEditing(false)
  }

  const answers = question.answer || []
  const selectedAnswer = answers.find(answer => answer.answerId === selectedAnswerId)

  let subtitleChild

  if (selectedAnswer) {
    const getColor = () => {
      if (question.outcome === QuizQuestion.Outcome.QQ_CORRECT) {
        return 'primary'
      } else if (question.outcome === QuizQuestion.Outcome.QQ_INCORRECT) {
        return 'error'
      } else {
        return undefined
      }
    }

    subtitleChild = (
      <>
        <T id="quiz_answer" />
        {': '}
        <Typography
          variant="body1"
          component="span"
          color={getColor()}
        >
          {selectedAnswer.longText}
        </Typography>
      </>
    )
  } else if (question.outcome === QuizQuestion.Outcome.QQ_UNKNOWN) {
    subtitleChild = <MarkdownTypography lexer="inline" markdown={question.subtitle} />
  } else {
    subtitleChild = '-'
  }

  return (
    <>
      <ListItem divider={!isEditing} dense>
        {question.iconUrl && <img alt="" src={question.iconUrl} className={classes.icon} />}
        <Grid item xs>
          <MarkdownTypography lexer="inline" variant="body2" markdown={question.title} />
          <Typography component="div" variant="body2">{subtitleChild}</Typography>
        </Grid>
        {question.editable && (
          <Grid item className={classes.buttonContainer}>
            {isEditing
              ? <Button variant="contained" onClick={stopEditing}><T id="quiz_action_done" /></Button>
              : <Button variant="contained" onClick={startEditing}><T id="quiz_action_edit" /></Button>
            }
          </Grid>
        )}
      </ListItem>
      {isEditing && (
        <ListItem divider dense disableGutters className={classes.answersContainer}>
          <QuizChoices
            answers={answers}
            selectedColor="primary"
            selectedAnswerId={selectedAnswerId}
            onAnswerChange={setSelectedAnswerId}
          />
        </ListItem>
      )}
    </>
  )
}

export default withStyles(styles)(QuizListItem)
